<script setup lang="ts">
import uniqBy from 'lodash.uniqby';
import {
  computed,
  defineOgImageComponent,
  onMounted,
  ref,
  useAsyncData,
  useCreators,
  useHead,
  useLocalePath,
  useMetrics,
  useRoute,
  useRouter,
  watch,
} from '#imports';

const localePath = useLocalePath();

// Fetch Creator
const route = useRoute();
const handle = route.params.handle as string;
const { fetchCreatorByHandle } = useCreators();
const {
  data: creator,
  pending: creatorPending,
  error: creatorError,
} = await useAsyncData(`creator-${handle}`, () => fetchCreatorByHandle(handle));
// Open Social Links in New Tab
const openInNewTab = (url: string) => {
  window.open(url, '_blank', 'noopener,noreferrer');
};

useHead(() => ({
  title: `${creator.value.display_name} - @${creator.value.mappr_handle}`,
  meta: [
    {
      name: 'description',
      content: creator.value.summary,
    },
  ],
}));

defineOgImageComponent('Default', {
  title: `${creator.value.display_name} - @${creator.value.mappr_handle}`,
  description: creator.value.summary,
  imagePath: creator.value.profile_image_url,
});

// On Load fix issue where page starts scrolled half way
onMounted(() => {
  window.scrollTo(0, 0);
  if (creator.value) {
    const { trackEvent } = useMetrics();
    trackEvent('viewed_profile', {
      creator_handle: creator.value.mappr_handle,
      creator_id: creator.value.id,
    });
  }
});

const categories = computed(() => {
  if (!creator.value || !creator.value.maps) return [];
  const allCategories = creator.value.maps.flatMap((map) =>
    map.spots.flatMap((spot) => spot.categories),
  );
  // Filter out null values
  const nonNullCategories = allCategories.filter(
    (category) => category != null,
  );
  return uniqBy(nonNullCategories, 'id');
});

const router = useRouter();
const handleBackButton = () => {
  router.back();
};
</script>

<template>
  <div>
    <MPButton icon="ri-arrow-left-s-line" hide-label label="back" class="back-button" rounded @click="handleBackButton" />
    <div v-if="creatorPending"/>
    <div v-if="creatorError && !creatorPending" class="profile-page">
      Could not find creator
    </div>
    <div v-if="creator && !creatorPending" class="profile-page">
      <div class="profile-image" :style="`background-image: url('${creator.profile_image_url}')`">
        <div class="social-links">
          <MPButton
            v-if="creator.tiktok_id"
            icon="ri-tiktok-line"
            label="tiktok"
            hide-label
            version="default"
            @click="openInNewTab(`https://www.tiktok.com/@${creator.tiktok_id}`)"
          />
          <MPButton
            v-if="creator.facebook_id"
            icon="ri-facebook-fill"
            label="facebook"
            hide-label
            version="default"
            @click="openInNewTab(`https://facebook.com/@${creator.facebook_id}`)"
          />
          <MPButton
            v-if="creator.instagram_id"
            icon="ri-instagram-line"
            label="instagram"
            hide-label
            version="default"
            @click="openInNewTab(`https://instagram.com/${creator.instagram_id}`)"
          />
          <MPButton
            v-if="creator.twitter_id"
            icon="ri-twitter-x-line"
            label="twitter/x"
            hide-label
            version="default"
            @click="openInNewTab(`https://twitter.com/${creator.twitter_id}`)"
          />
          <MPButton
            v-if="creator.youtube_id"
            icon="ri-youtube-fill"
            label="youtube"
            hide-label
            version="default"
            @click="openInNewTab(`https://youtube.com/@${creator.youtube_id}`)"
          />
        </div>
        <div class="stats">
          <div v-if="creator.spotsCount">
            <i class="ri-map-pin-fill" />
            &nbsp;{{ creator.spotsCount || 0 }}&nbsp;Spots
          </div>
          <div v-if="creator.maps">
            <i class="ri-map-2-line" />
            &nbsp;{{ creator.maps.length }}&nbsp;Map{{ creator.maps.length > 1 ? 's' : '' }}
          </div>
        </div>
      </div>
      <div class="profile-info">
        <div>
          <span v-if="creator.mappr_handle" class="profile-info-handle">
            @{{ creator.mappr_handle }}
          </span>
          <h1 v-if="creator.display_name" class="profile-info-title">
            {{ creator.display_name }}
          </h1>
          <p v-if="creator.summary" class="profile-info-summary summary">
            {{ creator.summary }}
          </p>
        </div>
        <div class="profile-map-cta">
          <div v-if="categories" class="map-categories">
            <div v-for="category in categories.slice(0, 5)" :key="category.id" class="map-category">
              <i :class="`${category.icon}`" />
              {{ category.name }}
            </div>
            <div v-if="categories.length > 6" class="map-category" style="color: var(--color-rose-300);margin-top:0.08rem;">
              <NuxtLinkLocale :to="`/maps/${creator.maps[0].current_slug}`">
                + {{ categories.length - 4 }} More
              </NuxtLinkLocale>
            </div>
          </div>
          <div v-if="creator.maps[0]" class="map-spots-preview">
            <NuxtLink class="map-cta" :to="localePath(`/maps/${creator.maps[0].current_slug}`)">
              {{ creator.maps[0].title }}
              <i class="ri-arrow-right-circle-line" />
            </NuxtLink>
            <div class="spot-cta">
              <NuxtLink v-for="spot in creator.maps[0].spots.slice(0, 2)" :key="spot.id" :to="localePath(`/maps/${creator.maps[0].current_slug}`)">
                <MPImg :src="spot.cover_image_url ? spot.cover_image_url : '/media/images/placeholder.jpg'" />
              </NuxtLink>
            </div>
          </div>
        </div>
      </div>
      <div v-if="creator.display_name && creator.bio_text" id="about">
        <!-- <h2>
          About {{ creator.display_name }}
        </h2> -->
        <p>
          {{ creator.bio_text }}
        </p>
      </div>
      <div v-if="creator.maps && creator.maps.length > 0">
        <div class="maps-list">
          <h2>Explore Maps</h2>
          <div v-for="map in creator.maps" :key="map.id">
            <MPMapCard :to="localePath(`/maps/${map.current_slug}`)" :map="map" version="inline" />
          </div>
        </div>
      </div>
      <div class="spots-preview">
        <div class="spots-preview-spots">
          <div v-if="creator.maps[0].spots[0]?.cover_image_url" :style="`background-image: url(${creator.maps[0].spots[0].cover_image_url})`" />
          <div v-if="creator.maps[0].spots[1]?.cover_image_url" :style="`background-image: url(${creator.maps[0].spots[1].cover_image_url})`" />
          <div v-if="creator.maps[0].spots[2]?.cover_image_url" :style="`background-image: url(${creator.maps[0].spots[2].cover_image_url})`" />
          <div v-if="creator.maps[0].spots[3]?.cover_image_url" :style="`background-image: url(${creator.maps[0].spots[3].cover_image_url})`" />
        </div>
      </div>
      <div v-if="creator.maps[0] && creator.maps[0].spots_count > 4" class="map-stack">
        <MPStack overlay>
          <template #main>
            <div class="map-card-large">
              <div>
                <div>
                  <h4>{{ creator.maps[0].title }}</h4>
                  <p><i class="ri-map-pin-fill" />&nbsp;{{ creator.maps[0].spots_count }} Spots</p>
                </div>
                <MPButton
                  version="primary"
                  label="Explore map"
                  icon="ri-map-2-line"
                  @click="router.push(localePath(`/maps/${creator.maps[0].current_slug}`))"
                />
              </div>
              <MPImg v-if="creator.maps[0].cover_image_url" :src="creator.maps[0].cover_image_url" class="cover" />
            </div>
          </template>
          <template #secondary>
            <MPImg v-if="creator.maps[0].spots[3]?.cover_image_url" :src="creator.maps[0].spots[3].cover_image_url" class="cover" />
          </template>
          <template #tertiary>
            <MPImg v-if="creator.maps[0].spots[4]?.cover_image_url" :src="creator.maps[0].spots[4].cover_image_url" class="cover" />
          </template>
        </MPStack>
        <MPSectionHeader
          title="A Guide for your next adventure"
          subtitle="Discover Unique Spots"
          :summary="`Discover the best spots and experiences from ${creator.display_name}. Get inspired by their unique stories and discover new places to explore.`"
          :center-title="false"
        />
      </div>
      <div v-if="creator.faqItems.length > 0" id="faq">
        <MPSectionHeader
          :title="$t('creators.faq.title')"
          :subtitle="$t('creators.faq.pill')"
          :summary="$t('creators.faq.getAnswers') + ' ' + creator.display_name + ' ' + $t('creators.faq.andGuides')"
        />
        <MPAccordion
          :items="creator.faqItems.map(item => {
            return {
              id: item.id,
              header: item.question,
              content: item.answer
            }
          })"
        />
      </div>
      <div class="profile-footer-cta">
        <MPFollow icon-only :creator="creator.id" />&nbsp;&nbsp;
        <MPButton
          rounded
          block
          color="rose"
          label="Explore map"
          icon="ri-map-2-line"
          @click="router.push(localePath(`/maps/${creator.maps[0].current_slug}`))"
        />
      </div>
    </div>
  </div>
</template>

<style lang="postcss" scoped>
@import "@/assets/css/typography.pcss";
@import "@/assets/css/breakpoints.pcss";
@import "@/assets/css/layouts.pcss";

.back-button {
  display: block !important;
  position: absolute !important;
  top: 0.75rem;
  left: 0.75rem;
  z-index: 9000;
  @mixin screen sm {
    display: none !important;
  }
}

.spots-preview {
  display: none;
  margin: 0;
  grid-column: span 2;
  @mixin screen sm {
    display: block;
    margin-bottom: 4rem;
  }
  h1 {
    margin-bottom: 6rem;
  }
  .spots-preview-spots {
    display: grid;
    grid-template-columns: repeat(4, 1fr);
    gap: 1rem;
    margin-top: 2rem;
    margin-left: -7rem;
    > div {
      width: 20rem;
      height: 25rem;
      background-color: var(--color-slate-700);
      border-radius: 0.5rem;
      background-size: cover;
      background-position: center;
      background-repeat: no-repeat;
    }
    > div:nth-child(odd) {
      margin-top: 5rem
    }

  }
}

#faq {
  grid-column: span 2;
  display: grid;
  grid-template-columns: 1fr;
  gap: 1rem;
  padding: 0 1rem 6rem;
  text-align: center;
  justify-items: center;
  width: 100%;
  @mixin screen sm {
    width: 40rem;
    margin: 0 auto;
  }
}

#about {
  grid-column: span 2;
  display: grid;
  padding: 0 1rem;
  max-width: 100vw;
  @mixin screen sm {
    padding: 8rem 0 6rem;
    text-align: center;
    max-width: 46rem;
    margin: 0 auto;
  }
  p {
    @mixin typeface-summary;
    color: var(--color-slate-500);
    line-height: 1.7 !important;
    text-wrap: balance;
  }
}

.map-card-large {
  position: relative;
  height: 30rem;
  > div {
    position: absolute;
    top: 0;
    left: 0;
    z-index: 1;
    width: 100%;
    height: 100%;
    padding: 1.5rem;
    color: white;
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    background: linear-gradient(-20deg, rgba(0, 0, 0, 0) 30%, rgba(0, 0, 0, 0.7) 100%);
  }
  img {
    position: relative;
    top: 0;
    left: 0;
    object-fit: cover;
    width: 100%;
    height: 100%;
    border-radius: 0.5rem;
  }
  i {
    color: var(--color-rose-500);
  }
}

.map-stack {
  display: none;
  gap: 1rem;
  grid-column: span 2;
  margin: 0 auto;
  @mixin screen sm {
    display: grid;
    grid-template-columns: 32rem 1fr;
    gap: 4rem;
    padding: 6rem 0;
    align-items: center;
  }
}

.map-categories {
  display: flex;
  flex-direction: row;
  gap: 0.75rem;
  overflow: hidden;
  max-width: fit-container;
  padding: 0 0 1rem;
  max-width: 90vw;
  overflow-x: scroll;
  scrollbar-width: none;
  &::-webkit-scrollbar {
      display: none;  /* Safari and Chrome */
  }
  .map-category {
    color: var(--color-slate-500);
    white-space: nowrap;
    i {
      color: var(--color-slate-500);
      font-size: 1.2rem;
      padding-right: 0.1rem;
    }
  }
}

.profile-content {
  grid-column: 1;
  @mixin screen sm {
    grid-row: 3;
    grid-column: span 2;
  }
}

.maps-list {
  gap: 1rem;
  padding: 1rem 1rem;
  @mixin screen sm {
    display: none;
  }
}

.profile-menu {
  display: none;
}

.profile-footer-cta {
  display: inline-flex;
  background-color: white;
  padding: 0.5rem 1rem;
  position: fixed;
  bottom: 0;
  left: 0;
  width: 100%;
  @mixin screen sm {
    display: none;
  }
  button + button {
    margin-left: 0.5rem;
  }
}


/* Refactored styles below */
.profile-page {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  z-index: 8000;
  display: grid;
  grid-template-columns: 1fr;
  gap: 1rem;
  background: white;
  @mixin screen sm {
    all: unset;
    @mixin layout-wrapper-wide;
    display: grid;
    margin-top: 4rem;
    grid-template-columns: 28rem 1fr;
    gap: 1rem;
  }
  @mixin screen lg {
    grid-template-columns: 28rem 1fr;
    max-width: 70rem;
  }
}

.profile-image {
  position: relative;
  background-size: cover;
  background-repeat: no-repeat;
  background-position: center center;
  width: 100vw;
  height: 60vh;
  @mixin screen sm {
    width: auto;
    height: auto;
    min-height: 30rem;
    border-radius: 0.5rem;
  }
}

.social-links {
  position: absolute;
  right: 0.75rem;
  top: 0.75rem;
  display: flex;
  flex-direction: row;
  gap: 0.5rem;
  button {
    border-color: transparent;
    background-color: rgba(0,0,0,0.6);
    &:deep(i) {
      color: var(--color-slate-50);
      font-size: 1.5rem;
      width: 1.5rem;
      height: 1.5rem;
    }
  }
}

.stats {
  position: absolute;
  bottom: 0;
  left: 0;
  width: 100%;
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 1rem;
  /* border-radius: 0.5rem; */
  background-color: rgba(0,0,0,0.6);
  color: var(--color-slate-100);
  padding: 0.75rem;
  text-align: center;
  border-radius: 0 0 0.5rem 0.5rem;
}

.profile-info {
  grid-column: 1;
  background-color: white;
  border-radius: 1rem 1rem 0 0;
  padding: 0 1rem;
  @mixin screen sm {
    padding: 1rem 0 0 0;
    align-items: flex-start;
    grid-row: 1;
    grid-column: 2;
    margin-top: 1rem;
    display: flex;
    flex-direction: column;
    justify-content: space-between;
  }
  .profile-info-handle {
    color: var(--color-rose-500);
    @mixin typeface-regular
  }
  .profile-info-title {
    /* line-height: 110%;
    margin: 1rem 0; */
  }
  .profile-info-summary {
    /* color: var(--color-slate-500);
    @mixin typeface-summary; */
  }
}

.profile-map-cta {
  margin: 2rem 0;
  @mixin screen sm {
    margin: 0;
    display: flex;
    flex-direction: column;
    align-items: start;
    justify-content: flex-end;
  }
  .map-spots-preview {
    display: grid;
    grid-template-columns: 1fr 1fr;
    max-width: 100%;
    overflow-x: scroll;
    scrollbar-width: none;
    &::-webkit-scrollbar {
        display: none;  /* Safari and Chrome */
    }
    @mixin screen sm {
      grid-template-columns: 1fr 2fr;
    }
    a:nth-child(2) {
      grid-column: span 2;
    }
  }
  .map-cta {
    background: var(--color-rose-400);
    color: var(--color-rose-50);
    padding: 1rem;
    min-height: 10rem;
    border-radius: 0.5rem;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: space-between;
    gap: 0.5rem;
    height: 100%;
    font-size: 1.15rem;
    i {
      font-size: 1.5rem;
      align-self: end;
    }
  }
  .spot-cta {
    display: flex;
    flex-direction: column;
    gap: 0.75rem;
    @mixin screen sm {
      flex-direction: row;
      gap: 0;
    }
    > a {
      padding-left: 0.75rem;
      display: flex;
    }
    img {
      max-width: 100%;
      height: auto;
      object-fit: cover;
      border-radius: 0.5rem;
    }
  }
}
</style>
